import { ref, Ref } from "vue";
import { allColumnKeys } from "@/ui/components/FileBrowser/columns";
import { ResourceTreeCategory } from "@/ui/components/FileBrowser/types";

/**
 * The persisted record. `known` is the set of column keys that existed the last
 * time this user saved: it is what lets a column added later default to visible
 * rather than looking like one they deliberately hid.
 */
type StoredColumnPreferences = {
	visible: string[];
	known: string[];
};

/**
 * Never hideable. The name cell carries the folder chevron, the read-only badge
 * and the favourite tint, and every other cell renders an em-dash for folder
 * rows - without it a folder is an unidentifiable, unnavigable row.
 */
const LOCKED_KEYS = ["title"];

/** The curated starting set, rather than every column at once. */
export const DEFAULT_KEYS = ["title", "created_at", "last_modified_at"];

// One record per tree type, so the user can have different preferences for each
const STORAGE_KEY = "fileBrowser.columns";

export const useColumnPreferences = (type: ResourceTreeCategory) => {
	const storageKey = STORAGE_KEY + "." + type;
	const readStored = (): StoredColumnPreferences | null => {
		const raw = localStorage.getItem(storageKey);

		if (!raw) {
			return null;
		}

		try {
			const parsed = JSON.parse(raw);

			if (
				!parsed ||
				!Array.isArray(parsed.visible) ||
				!Array.isArray(parsed.known)
			) {
				return null;
			}

			return parsed;
		} catch {
			return null;
		}
	};

	/**
	 * Collapses the stored record into the effective set, once. Iterating the
	 * canonical keys keeps FILE_COLUMNS order, drops keys left over from deleted
	 * columns, and makes duplicates impossible.
	 */
	const load = (): string[] => {
		const stored = readStored();

		if (!stored) {
			return allColumnKeys();
		}

		return allColumnKeys().filter(
			(key) =>
				stored.visible.includes(key) ||
				!stored.known.includes(key) ||
				LOCKED_KEYS.includes(key)
		);
	};

	/** The effective set: always complete and concrete, never a partial record. */
	const visibleKeys: Ref<string[]> = ref(load());

	/** `known` is rewritten from the current column set, never from state. */
	const save = (): void => {
		try {
			localStorage.setItem(
				storageKey,
				JSON.stringify({
					visible: visibleKeys.value,
					known: allColumnKeys(),
				})
			);
		} catch {
			// Quota or private-mode failures: a cosmetic preference is never worth
			// breaking the table over.
		}
	};

	const isColumnVisible = (key: string): boolean =>
		visibleKeys.value.includes(key);

	const isColumnLocked = (key: string): boolean => LOCKED_KEYS.includes(key);

	const toggleColumn = (key: string): void => {
		if (isColumnLocked(key)) {
			return;
		}

		visibleKeys.value = isColumnVisible(key)
			? visibleKeys.value.filter((visibleKey) => visibleKey !== key)
			: allColumnKeys().filter(
					(columnKey) => columnKey === key || isColumnVisible(columnKey)
			  );

		save();
	};

	/** Writes the curated set back, so a reset survives a reload. */
	const resetColumnPreferences = (): void => {
		try {
			localStorage.setItem(
				storageKey,
				JSON.stringify({
					visible: DEFAULT_KEYS,
					known: allColumnKeys(),
				})
			);
		} catch {
			// Ignored - the in-memory reset below is what the user actually sees.
		}

		visibleKeys.value = [...DEFAULT_KEYS];
	};

	return {
		visibleKeys,
		isColumnVisible,
		isColumnLocked,
		toggleColumn,
		resetColumnPreferences,
	};
};
